// The interconnect hub — the page's centerpiece and its fold boundary: the journal at the hub, the three pillars around it, and every edge labeled with the mechanism that actually implements it.
// EVERY EDGE LABEL IS A VERIFIED CLAIM. Do not soften, embellish or add one without re-reading the code:
//   Journal → Series   `stories/generate.ts` assembleSharedEntryContext + `data/models/entries.ts` postChapter (chapters are written back as entries, with no entries_tags row).
//   Fields → Series    `generate.ts` `const fieldContext = isWholeJournal ? fieldContextAll : undefined`, where `isWholeJournal = scope === 'all' && !tagIds.length`. The whole-journal condition is part of the claim — dropping it makes the sentence false for a tag-scoped Series.
//   Community → Series all three privacy tiers feed the recipient's prompt at tier-scaled depth (`docs/gnothi/sharing.md`). NOT "real people become characters": `shared/utils/ledgerEdit.ts` gates character rows on the fiction/spoilers path, so that half is fiction-only.
//   Series → world     `docs/gnothi/podcasts.md`: narration is forced and free, `attachDerived` mints the podcasts row, so there is no enable route. The RSS feed is the ONLY ungated public tier — `data/schemas/podcasts.ts`'s privacy comment and `routes/tts/privacy-gate.ts` put `pages` behind is_cool and the front-page directory behind is_superuser, so this edge must never promise the directory (an earlier draft did). There is NO Series→Group edge either — sharing is tag-scoped and chapter rows carry no tags row, so a Series cannot reach a Group that way.
// Node-prerender-safe: MUI + react-router only.
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import EditNoteIcon from '@mui/icons-material/EditNoteOutlined';
import MenuBookIcon from '@mui/icons-material/MenuBookOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import PodcastsIcon from '@mui/icons-material/PodcastsOutlined';
import { featureColors } from '../../src/ui/Setup/Mui';
import { SectionHeader, accentColor } from './splash-sections';

/** A labeled edge. The arrow glyph carries the direction, the rule under it carries the "this is an edge, not a bullet" reading, and `label` is the claim. */
function Edge({ arrow, label, children }: { arrow: string; label: string; children: ReactNode }) {
  return (
    <Box sx={{ px: 1 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', mb: 0.5 }}>
        <Typography component="span" aria-hidden sx={{ color: 'primary.main', fontWeight: 700, lineHeight: 1 }}>{arrow}</Typography>
        <Typography variant="overline" sx={{ color: accentColor(), fontWeight: 700, letterSpacing: 0.6, lineHeight: 1.4 }}>{label}</Typography>
      </Stack>
      <Box sx={{ borderTop: '2px dashed', borderColor: 'primary.main', opacity: 0.35, mb: 1 }} />
      <Typography variant="body2" color="text.secondary">{children}</Typography>
    </Box>
  );
}

/** A corner wash in the node's own accent — the tint is built from `featureColors`, one of the two sanctioned escapes from "no hex on a page", and `home-pillars`' BentoCard already composes its glow the same way. Decorative only. */
const glow = (accent: string) => ({
  content: '""', position: 'absolute' as const, top: -40, right: -40, width: 160, height: 160,
  borderRadius: '50%', background: `radial-gradient(circle, ${accent} 0%, transparent 60%)`, opacity: 0.16, pointerEvents: 'none' as const,
});

/** `hero` marks the node every edge terminates at (Series). It takes the elevated surface the vocabulary reserves for a section's single hero/promo, so the convergence reads as convergence at a glance instead of only in the labels. */
function Node({ icon, title, to, accent, hero = false, children }: { icon: ReactNode; title: string; to?: string; accent: string; hero?: boolean; children: ReactNode }) {
  const body = (
    <>
      <Box sx={{ p: 1.25, mb: 1.5, borderRadius: 2.5, width: 'fit-content', display: 'inline-flex', bgcolor: `${accent}1f`, color: accent, '& > svg': { fontSize: 34 } }}>{icon}</Box>
      <Typography variant="h6" component="h3" sx={{ fontWeight: 700 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary">{children}</Typography>
    </>
  );
  return (
    <Card
      variant={hero ? 'elevation' : 'outlined'}
      sx={{
        p: { xs: 2.5, md: 3 }, borderRadius: 4, height: '100%', position: 'relative', overflow: 'hidden',
        border: '2px solid', borderColor: accent, '&::after': glow(accent),
        ...(hero && { boxShadow: '0 18px 44px -18px rgba(10,17,40,0.35)' }),
        ...(to && { transition: 'transform .2s ease, box-shadow .2s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: 8 } }),
      }}
    >
      {to ? <Box component={RouterLink} to={to} sx={{ textDecoration: 'none', color: 'inherit', display: 'block', position: 'relative' }}>{body}</Box> : body}
    </Card>
  );
}

/**
 * Grid geometry, md and up: the Journal hub and the two other sources across the top row, one labeled edge cell under each, all three converging on the full-width Series card, and a last edge out of it to listeners. It is drawn as a convergence rather than a radial wheel because that is what the verified edge set actually is — all three inbound edges terminate at Series, and only Series has an outbound one.
 * On xs it collapses to one column in reading order, which is why each edge label owns a real grid cell instead of being an absolutely-positioned connector line: a positioned line cannot reflow, and this section has to survive a phone.
 */
export function HomeInterconnect() {
  return (
    <>
      <SectionHeader
        eyebrow="The whole product in one picture"
        title="One journal, three pillars, and the seams between them"
        deck={<><strong>Series</strong> author your real entries into an ongoing novel, <strong>Fields</strong> track what actually moves you, and <strong>Community</strong> lends your story perspectives that aren't your own. What you write is the source, and every pillar reads from it and writes back into it.</>}
      />
      <Box
        sx={{
          display: 'grid',
          gap: { xs: 2, md: 3 },
          alignItems: 'stretch',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
          gridTemplateAreas: {
            xs: '"hub" "eSeries" "series" "fields" "eFields" "community" "eCommunity" "eWorld" "world"',
            md: `"fields hub community"
                 "eFields eSeries eCommunity"
                 "series series series"
                 ".      eWorld  ."
                 ".      world   ."`,
          },
        }}
      >
        <Box sx={{ gridArea: 'hub' }}>
          {/* The hub spells `color` on its own `sx` deliberately: MuiCard-root paints `color:var(--mui-palette-text-primary)` (#0f172a), so a dark-surfaced Card that only sets `bgcolor` ships near-black on navy. */}
          <Card sx={{ p: 3, height: '100%', borderRadius: 4, textAlign: 'center', bgcolor: 'background.elevated', color: 'common.white', position: 'relative', overflow: 'hidden', boxShadow: '0 18px 44px -18px rgba(10,17,40,0.45)', '&::after': { content: '""', position: 'absolute', top: -50, left: '50%', transform: 'translateX(-50%)', width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.28) 0%, transparent 65%)', pointerEvents: 'none' } }}>
            <Box sx={{ position: 'relative' }}>
              <EditNoteIcon sx={{ fontSize: 44 }} />
              <Typography variant="h6" component="h3" sx={{ fontWeight: 700 }}>Your journal</Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                Plain entries, in your own words. Everything below reads from here - there is nothing else to set up.
              </Typography>
            </Box>
          </Card>
        </Box>

        <Box sx={{ gridArea: 'eSeries' }}>
          <Edge arrow="↓" label="Journal → Series">
            Your entries are the source text. Gnothi authors Chapters out of them, and each Chapter is saved back into your journal as an entry of its own.
          </Edge>
        </Box>
        <Box sx={{ gridArea: 'series' }}>
          <Node icon={<MenuBookIcon />} title="Series" to="/series" accent={featureColors.prompt} hero>
            Where all three arrive: an ongoing book, written Chapter by Chapter out of everything above, in the genre you chose.
          </Node>
        </Box>

        <Box sx={{ gridArea: 'eFields' }}>
          <Edge arrow="↓" label="Fields → Series">
            What you track becomes context the author reads - for a Series drawing on your whole journal, with no tag scoping. Narrow a Series to a few tags and it writes from those entries alone.
          </Edge>
        </Box>
        <Box sx={{ gridArea: 'fields' }}>
          <Node icon={<InsightsOutlinedIcon />} title="Fields" to="/fields" accent={featureColors.behavior}>
            What you track, and what it moves.
          </Node>
        </Box>

        <Box sx={{ gridArea: 'eCommunity' }}>
          <Edge arrow="↓" label="Community → Series">
            Entries someone shares with you fold into your arc, at the depth their privacy tier allows and stamped with who wrote them. A second perspective, not just your own.
          </Edge>
        </Box>
        <Box sx={{ gridArea: 'community' }}>
          <Node icon={<PeopleOutlinedIcon />} title="Community" to="/community" accent={featureColors.themes}>
            The people you let into the story.
          </Node>
        </Box>

        <Box sx={{ gridArea: 'eWorld' }}>
          <Edge arrow="↓" label="Series → the world">
            Every Series is already a podcast - narration is automatic and free. Chapters become episodes on a real RSS feed, on every account, that you or your listeners can subscribe to in any podcast app.
          </Edge>
        </Box>
        <Box sx={{ gridArea: 'world' }}>
          <Node icon={<PodcastsIcon />} title="Listeners" to="/series" accent={featureColors.organization}>
            Your book, narrated, wherever you listen.
          </Node>
        </Box>
      </Box>
    </>
  );
}
